import React, { useState, useEffect } from 'react';
import { 
  TruckIcon, 
  BuildingOfficeIcon, 
  CurrencyDollarIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CloudArrowDownIcon,
  CalculatorIcon,
  EyeIcon,
  CogIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';
import ShippingPriceModal from './ShippingPriceModal';
import ShippingSettingsModal from './ShippingSettingsModal';

const ShippingCompaniesPanel = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCompany, setEditingCompany] = useState(null);
  const [editForm, setEditForm] = useState({
    city_price: '',
    intercity_price: '',
    desi_price: '',
    is_active: true
  });

  // Tarih formatını düzenleyen yardımcı fonksiyon
  const formatDate = (dateString) => {
    if (!dateString) return 'Bilinmiyor';
    
    try {
      // Farklı tarih formatlarını dene
      let date;
      
      // ISO string formatı (2024-01-15T10:30:00.000Z)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        date = new Date(dateString);
      }
      // Timestamp formatı (1705312200000)
      else if (typeof dateString === 'number' || (typeof dateString === 'string' && !isNaN(dateString))) {
        date = new Date(parseInt(dateString));
      }
      // Diğer string formatları
      else {
        date = new Date(dateString);
      }
      
      // Geçerli tarih kontrolü
      if (isNaN(date.getTime())) {
        console.warn('Geçersiz tarih formatı:', dateString);
        return 'Geçersiz tarih';
      }
      
      // Türkçe tarih formatında döndür
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Tarih formatlanırken hata:', error, 'Tarih:', dateString);
      return 'Hatalı tarih';
    }
  };
  
  // Modal state'leri
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Ayarlar modal state'leri
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedSettingsCompany, setSelectedSettingsCompany] = useState(null);

  // Kargo şirketlerini listele
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/shipping-companies/list');
      
      if (response.success) {
        setCompanies(response.data);
      } else {
        setError('Kargo şirketleri yüklenemedi');
      }
    } catch (error) {
      console.error('Kargo şirketleri yüklenirken hata:', error);
      setError(error.message || 'Kargo şirketleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Kargonomi'den kargo şirketlerini çek
  const fetchFromKargonomi = async () => {
    try {
      setFetchLoading(true);
      setError('');
      setSuccess('');
      
      const response = await api.get('/api/shipping-companies/fetch-companies');
      
      if (response.success) {
        setSuccess(response.message);
        await fetchCompanies(); // Listeyi yenile
      } else {
        setError('Kargo şirketleri çekilemedi');
      }
    } catch (error) {
      console.error('Kargonomi\'den çekerken hata:', error);
      setError(error.message || 'Kargo şirketleri çekilemedi');
    } finally {
      setFetchLoading(false);
    }
  };

  // Tüm fiyatları güncelle
  const updateAllPrices = async () => {
    try {
      setFetchLoading(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/api/shipping-companies/update-all-prices');
      
      if (response.success) {
        setSuccess('Tüm kargo şirketlerinin fiyatları başarıyla güncellendi!');
        await fetchCompanies(); // Listeyi yenile
      } else {
        setError('Fiyatlar güncellenemedi');
      }
    } catch (error) {
      console.error('Fiyat güncelleme hatası:', error);
      setError(error.message || 'Fiyatlar güncellenemedi');
    } finally {
      setFetchLoading(false);
    }
  };

  // Fiyat hesaplamalarını yeniden tetikle
  const recalculatePrices = async () => {
    try {
      setFetchLoading(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/api/shipping-companies/recalculate-prices');
      
      if (response.success) {
        setSuccess(`${response.updated_count} fiyat kaydı yeniden hesaplandı!`);
        await fetchCompanies(); // Listeyi yenile
      } else {
        setError('Fiyat hesaplamaları güncellenemedi');
      }
    } catch (error) {
      console.error('Fiyat hesaplama hatası:', error);
      setError(error.message || 'Fiyat hesaplamaları güncellenemedi');
    } finally {
      setFetchLoading(false);
    }
  };

  // Düzenleme formunu aç
  const startEditing = (company) => {
    setEditingCompany(company.id);
    setEditForm({
      city_price: company.city_price || '',
      intercity_price: company.intercity_price || '',
      desi_price: company.desi_price || '',
      is_active: company.is_active
    });
  };

  // Düzenlemeyi iptal et
  const cancelEditing = () => {
    setEditingCompany(null);
    setEditForm({
      city_price: '',
      intercity_price: '',
      desi_price: '',
      is_active: true
    });
  };

  // Kargo şirketini güncelle
  const updateCompany = async (companyId) => {
    try {
      setError('');
      setSuccess('');
      
      const response = await api.put(`/api/shipping-companies/${companyId}`, editForm);
      
      if (response.success) {
        setSuccess('Kargo şirketi başarıyla güncellendi');
        setEditingCompany(null);
        await fetchCompanies(); // Listeyi yenile
      } else {
        setError('Güncelleme başarısız');
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      setError(error.message || 'Güncelleme başarısız');
    }
  };

  // Form değişikliklerini handle et
  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Modal açma fonksiyonu
  const openPriceModal = (company) => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  // Modal kapatma fonksiyonu
  const closePriceModal = () => {
    setModalOpen(false);
    setSelectedCompany(null);
  };

  // Ayarlar modal açma fonksiyonu
  const openSettingsModal = (company) => {
    setSelectedSettingsCompany(company);
    setSettingsModalOpen(true);
  };

  // Ayarlar modal kapatma fonksiyonu
  const closeSettingsModal = () => {
    setSettingsModalOpen(false);
    setSelectedSettingsCompany(null);
  };

  // Ayarlar güncellendiğinde listeyi yenile
  const handleSettingsUpdated = async () => {
    console.log('🔄 Ayarlar güncellendi, şirket listesi yenileniyor...');
    setLoading(true);
    try {
      await fetchCompanies();
      console.log('✅ Şirket listesi başarıyla yenilendi');
    } catch (error) {
      console.error('❌ Şirket listesi yenilenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sayfa yüklendiğinde kargo şirketlerini getir
  useEffect(() => {
    fetchCompanies();
  }, []);

  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-2xl mb-4 shadow-lg">
            <BuildingOfficeIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-text-dark mb-2">Kargo Şirketleri</h1>
          <p className="text-text-light text-lg max-w-2xl mx-auto">
            Kargo şirketlerini yönetin, fiyatlarını güncelleyin ve ayarlarını düzenleyin
          </p>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-modern p-6 mb-8 animate-slide-in-left">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={fetchFromKargonomi}
              disabled={fetchLoading}
              className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-brand-green to-brand-green-light text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {fetchLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin w-5 h-5 mr-2" />
                  <span className="hidden sm:inline">İşleniyor...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <CloudArrowDownIcon className="w-5 h-5 mr-2 group-hover:animate-bounce" />
                  <span className="hidden sm:inline">Kargonomi'den Çek</span>
                  <span className="sm:hidden">Çek</span>
                </>
              )}
            </button>

            <button
              onClick={updateAllPrices}
              disabled={fetchLoading}
              className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {fetchLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin w-5 h-5 mr-2" />
                  <span className="hidden sm:inline">Güncelleniyor...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <CurrencyDollarIcon className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                  <span className="hidden sm:inline">Fiyatları Güncelle</span>
                  <span className="sm:hidden">Fiyat</span>
                </>
              )}
            </button>
            
            <button
              onClick={recalculatePrices}
              disabled={fetchLoading}
              className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {fetchLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin w-5 h-5 mr-2" />
                  <span className="hidden sm:inline">Hesaplanıyor...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <CalculatorIcon className="w-5 h-5 mr-2 group-hover:animate-bounce" />
                  <span className="hidden sm:inline">KDV & Komisyon</span>
                  <span className="sm:hidden">Hesap</span>
                </>
              )}
            </button>

            <button
              onClick={fetchCompanies}
              disabled={loading}
              className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="animate-spin w-5 h-5 mr-2" />
                  <span className="hidden sm:inline">Yenileniyor...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-5 h-5 mr-2 group-hover:animate-spin" />
                  <span className="hidden sm:inline">Listeyi Yenile</span>
                  <span className="sm:hidden">Yenile</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-r-xl animate-bounce-in">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded-r-xl animate-bounce-in">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-green-700 font-medium">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Companies Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-modern p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-16 animate-fade-in">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <TruckIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-text-dark mb-2">Henüz kargo şirketi yok</h3>
            <p className="text-text-light mb-6 max-w-md mx-auto">
              Kargonomi'den kargo şirketlerini çekmek için yukarıdaki "Kargonomi'den Çek" butonunu kullanın.
            </p>
            <button
              onClick={fetchFromKargonomi}
              disabled={fetchLoading}
              className="inline-flex items-center px-6 py-3 bg-brand-green text-white rounded-xl font-semibold hover:bg-brand-green-light transform hover:scale-105 transition-all duration-300"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Kargo Şirketlerini Ekle
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company, index) => (
              <div 
                key={company.id} 
                className="bg-white rounded-2xl shadow-modern hover:shadow-modern-lg transition-all duration-300 overflow-hidden group animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Company Header */}
                <div className="bg-gradient-to-r from-brand-header to-white p-6 border-b border-border-color">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center overflow-hidden">
                        {company.logo_url ? (
                          <img 
                            src={company.logo_url} 
                            alt={company.name}
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <TruckIcon className="w-6 h-6 text-brand-green" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-text-dark text-lg group-hover:text-brand-green transition-colors duration-300">
                          {company.name}
                        </h3>
                        <p className="text-sm text-text-light">#{company.kargonomi_id}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openSettingsModal(company)}
                        className="w-8 h-8 bg-white hover:bg-brand-green hover:text-white rounded-lg flex items-center justify-center transition-all duration-300 group/btn shadow-sm"
                        title="Ayarlar"
                      >
                        <CogIcon className="w-4 h-4 group-hover/btn:rotate-90 transition-transform duration-300" />
                      </button>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-300 ${
                        company.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.is_active ? 'Aktif' : 'Pasif'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Details */}
                <div className="p-6">
                  {editingCompany === company.id ? (
                    /* Edit Form */
                    <div className="space-y-4 animate-fade-in">
                      <div>
                        <label className="block text-sm font-medium text-text-dark mb-2">Şehir İçi Fiyat (₺)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.city_price}
                          onChange={(e) => handleFormChange('city_price', e.target.value)}
                          className="w-full px-4 py-3 border border-border-color rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300"
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-text-dark mb-2">Şehir Dışı Fiyat (₺)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.intercity_price}
                          onChange={(e) => handleFormChange('intercity_price', e.target.value)}
                          className="w-full px-4 py-3 border border-border-color rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300"
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-text-dark mb-2">Desi Fiyatı (₺)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.desi_price}
                          onChange={(e) => handleFormChange('desi_price', e.target.value)}
                          className="w-full px-4 py-3 border border-border-color rounded-xl focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all duration-300"
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.is_active}
                            onChange={(e) => handleFormChange('is_active', e.target.checked)}
                            className="w-5 h-5 rounded border-border-color text-brand-green focus:ring-brand-green transition-all duration-300"
                          />
                          <span className="text-sm font-medium text-text-dark">Aktif durumda</span>
                        </label>
                      </div>
                      
                      <div className="flex space-x-3 pt-4">
                        <button
                          onClick={() => updateCompany(company.id)}
                          className="flex-1 bg-brand-green text-white py-3 px-4 rounded-xl font-semibold hover:bg-brand-green-light transform hover:scale-105 transition-all duration-300"
                        >
                          Kaydet
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-xl font-semibold hover:bg-gray-600 transform hover:scale-105 transition-all duration-300"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Display Mode */
                    <div className="space-y-4">
                      {/* Fiyat Özeti */}
                      {company.shipping_company_prices && company.shipping_company_prices.length > 0 ? (
                        <div className="bg-brand-header rounded-xl p-4">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="text-sm font-semibold text-text-dark">Fiyat Tablosu</h4>
                            <span className="text-xs bg-brand-green text-white px-2 py-1 rounded-full font-medium">
                              {company.shipping_company_prices.length} Desi
                            </span>
                          </div>
                          
                          {/* İlk 3 Desi Özet */}
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            {company.shipping_company_prices.slice(0, 3).map((price) => (
                              <div key={price.desi} className="bg-white rounded-lg p-2 text-center shadow-sm">
                                <div className="text-xs text-text-light font-medium">{price.desi} Desi</div>
                                <div className="text-xs text-brand-green font-bold">₺{parseFloat(price.city_price_final || price.city_price || 0).toFixed(0)}</div>
                                <div className="text-xs text-brand-green-light font-bold">₺{parseFloat(price.intercity_price_final || price.intercity_price || 0).toFixed(0)}</div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Detaylı Görüntüleme Butonu */}
                          <button
                            onClick={() => openPriceModal(company)}
                            className="w-full bg-brand-green text-white py-2 px-4 rounded-lg font-semibold hover:bg-brand-green-light transition-all duration-300 transform hover:scale-105 text-sm group/btn"
                          >
                            <EyeIcon className="w-4 h-4 inline mr-2 group-hover/btn:animate-pulse" />
                            Detaylı Fiyat Tablosunu Görüntüle
                          </button>
                        </div>
                      ) : (
                        /* Eski Format (Fallback) */
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                              <p className="text-xs text-blue-600 font-semibold mb-1 flex items-center">
                                <TruckIcon className="w-3 h-3 mr-1" />
                                Şehir İçi
                              </p>
                              <p className="text-lg font-bold text-blue-800">₺{parseFloat(company.city_price || 0).toFixed(2)}</p>
                            </div>
                            <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
                              <p className="text-xs text-purple-600 font-semibold mb-1 flex items-center">
                                <TruckIcon className="w-3 h-3 mr-1" />
                                Şehir Dışı
                              </p>
                              <p className="text-lg font-bold text-purple-800">₺{parseFloat(company.intercity_price || 0).toFixed(2)}</p>
                            </div>
                          </div>
                          
                          <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                            <p className="text-xs text-orange-600 font-semibold mb-1 flex items-center">
                              <CurrencyDollarIcon className="w-3 h-3 mr-1" />
                              Desi Fiyatı
                            </p>
                            <p className="text-lg font-bold text-orange-800">₺{parseFloat(company.desi_price || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      )}
                      
                      {company.delivery_time && (
                        <div className="bg-brand-header rounded-xl p-3 border border-border-color">
                          <p className="text-xs text-text-light font-semibold mb-1 flex items-center">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            Teslimat Süresi
                          </p>
                          <p className="text-sm text-text-dark font-medium">{company.delivery_time}</p>
                        </div>
                      )}
                      
                      <div className="text-xs text-text-light flex items-center justify-between pt-2 border-t border-border-color">
                        <span>Son güncelleme:</span>
                        <span className="font-medium">{formatDate(company.last_updated || company.updated_at || company.created_at)}</span>
                      </div>

                      {/* Edit Button */}
                      <button
                        onClick={() => startEditing(company)}
                        className="w-full mt-4 bg-gray-100 hover:bg-brand-green hover:text-white text-text-dark py-2 px-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                      >
                        Düzenle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fiyat Detay Modal'ı */}
      <ShippingPriceModal 
        isOpen={modalOpen}
        onClose={closePriceModal}
        company={selectedCompany}
      />

      {/* Ayarlar Modal'ı */}
      <ShippingSettingsModal 
        isOpen={settingsModalOpen}
        onClose={closeSettingsModal}
        company={selectedSettingsCompany}
        onSettingsUpdated={handleSettingsUpdated}
      />
    </div>
  );
};

export default ShippingCompaniesPanel;